import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plug, RefreshCw, Truck, CreditCard } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDate } from "@/lib/api";
import { PageHeader, StatusBadge, DemoBadge, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";

export default function Integrations() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["int-health"], queryFn: async () => (await api.get("/admin/integrations/health")).data, refetchInterval: 30000 });
  const { data: jobs } = useQuery({ queryKey: ["int-jobs"], queryFn: async () => (await api.get("/admin/integrations/jobs?limit=30")).data });

  const retry = useMutation({
    mutationFn: async (id) => (await api.post(`/admin/integrations/jobs/${id}/retry`)).data,
    onSuccess: (r) => {
      if (r.ok) toast.success("Job retried successfully");
      else toast.error(r.error || "Retry failed");
      qc.invalidateQueries({ queryKey: ["int-jobs"] });
    },
  });

  if (!data) return <p className="text-sm text-slate-500">Loading…</p>;

  return (
    <div data-testid="integrations-page">
      <PageHeader title="Integration Health" sub="Marketplace, shipping and payment connections. Credentials never leave the server." testid="integrations-title">
        <DemoBadge label="ALL INTEGRATIONS IN DEMO MODE" />
      </PageHeader>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl border border-slate-800 bg-[#111827] p-5" data-testid="health-indiafast">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><Truck size={15} className="text-orange-400" /><h3 className="text-sm font-semibold text-slate-100">IndiaFast Shipping</h3></div>
            <span className={`h-2 w-2 rounded-full ${data.indiafast.status === "connected" ? "bg-emerald-400" : "bg-rose-400"}`} />
          </div>
          <p className="mt-3 text-xs text-slate-400">Status: <span className="capitalize text-emerald-400">{data.indiafast.status}</span></p>
          <p className="text-xs text-slate-500">Mode: <span className="uppercase text-violet-300">{data.indiafast.mode}</span></p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-[#111827] p-5" data-testid="health-razorpay">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><CreditCard size={15} className="text-blue-400" /><h3 className="text-sm font-semibold text-slate-100">Razorpay</h3></div>
            <span className={`h-2 w-2 rounded-full ${data.razorpay.mode === "live" ? "bg-emerald-400" : "bg-violet-400"}`} />
          </div>
          <p className="mt-3 text-xs text-slate-400">Mode: <span className="uppercase text-violet-300">{data.razorpay.mode}</span></p>
          <p className="text-xs text-slate-500">{data.razorpay.mode === "live" ? "Live keys configured" : "No keys — mock checkout active"}</p>
        </div>
        {["amazon", "flipkart"].map((ch) => {
          const accs = data.accounts.filter((a) => a.channel === ch);
          const connected = accs.filter((a) => a.status === "connected").length;
          return (
            <div key={ch} className="rounded-xl border border-slate-800 bg-[#111827] p-5" data-testid={`health-${ch}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Plug size={15} className="text-amber-400" /><h3 className="text-sm font-semibold capitalize text-slate-100">{ch}</h3></div>
                <span className={`h-2 w-2 rounded-full ${connected ? "bg-emerald-400" : "bg-slate-600"}`} />
              </div>
              <p className="mt-3 text-xs text-slate-400">{connected} connected account{connected === 1 ? "" : "s"}</p>
              <p className="text-xs text-slate-500">{accs.length - connected} need action</p>
            </div>
          );
        })}
      </div>

      <div className="mt-6 rounded-xl border border-slate-800 bg-[#111827] p-5" data-testid="marketplace-accounts">
        <h3 className="mb-3 text-sm font-semibold text-slate-200">Marketplace Accounts</h3>
        {!data.accounts.length ? <EmptyState title="No marketplace accounts" sub="Configure from an Agent's detail page." /> : (
          <div className="space-y-2">
            {data.accounts.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-900/60 px-3 py-2 text-xs">
                <div className="flex items-center gap-3">
                  <span className="font-medium capitalize text-slate-200">{a.channel}</span>
                  <span className="text-slate-500">{a.agent_name}</span>
                  <span className="font-mono text-[10px] text-slate-500">{a.seller_id || "—"}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] text-slate-500">{a.last_sync ? `Synced ${fmtDate(a.last_sync)}` : "Never synced"}</span>
                  <StatusBadge status={a.status} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4 rounded-xl border border-slate-800 bg-[#111827] p-5" data-testid="integration-jobs">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-200">Sync Jobs</h3>
          <p className="text-[10px] text-slate-500">Pending: {data.jobs_pending} · Failed: {data.jobs_failed}</p>
        </div>
        {!jobs?.items?.length ? <EmptyState title="No jobs yet" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-800 text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2">Time</th>
                  <th className="px-3 py-2">Integration</th>
                  <th className="px-3 py-2">Operation</th>
                  <th className="px-3 py-2">Agent</th>
                  <th className="px-3 py-2">Records</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {jobs.items.map((j) => (
                  <tr key={j.id} data-testid={`job-row-${j.id}`} className="border-b border-slate-800/50">
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-[10px] text-slate-500">{fmtDate(j.created_at)}</td>
                    <td className="px-3 py-2 capitalize text-slate-300">{j.integration}</td>
                    <td className="px-3 py-2 text-slate-400">{j.operation}</td>
                    <td className="px-3 py-2 text-slate-400">{j.agent_name || "—"}</td>
                    <td className="px-3 py-2 text-slate-400">{j.success}/{j.processed}</td>
                    <td className="px-3 py-2">
                      <StatusBadge status={j.status} />
                      {j.error && <p className="mt-0.5 max-w-56 truncate text-[10px] text-rose-400" title={j.error}>{j.error}</p>}
                    </td>
                    <td className="px-3 py-2">
                      {j.status === "failed" && (
                        <Button data-testid={`retry-job-${j.id}`} size="sm" variant="outline" onClick={() => retry.mutate(j.id)}
                          className="border-slate-700 text-slate-300 hover:bg-slate-800"><RefreshCw size={11} /> Retry</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
